import Rollbar from "rollbar"
import { env } from "@/lib/env"
import { tokenManager } from "@/lib/token-manager"

const APP_NAME = "Commission Dashboard MFE"
const APP_TAGS = "commission-dashboard"

function deriveTitle(payload: any): string | null {
  const trace = payload.body?.trace ?? payload.body?.trace_chain?.[0]
  if (trace?.exception) {
    return `${trace.exception.class}: ${trace.exception.message}`
  }
  const message = payload.body?.message?.body
  return message ?? null
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
    return JSON.parse(atob(padded))
  } catch (e) {
    console.warn("Failed to decode JWT payload", e)
    return null
  }
}

// Treat unresolved CI placeholders (#{...}#) as absent
const accessToken = env.rollbarAccessToken.startsWith("#{") ? "" : env.rollbarAccessToken

export const rollbar = new Rollbar({
  accessToken,
  environment: env.rollbarEnvironment,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: env.rollbarEnabled && !!accessToken,
  checkIgnore(_isUncaught, _args, payload) {
    // React 19 Strict Mode double-invokes effects in development, causing the same error to fire
    // twice with different internal React stack frames. Suppress the duplicate invocation to
    // prevent two separate Rollbar items being created for a single error.
    const body = (payload as any).body
    const traces: any[] = body?.trace_chain ?? (body?.trace ? [body.trace] : [])
    const frames: any[] = traces.flatMap((t: any) => t?.frames ?? [])
    return frames.some((f: any) => f.method?.includes("DoubleInvokeEffectsInDEV"))
  },
  transform(payload: any) {
    const title = payload.title ?? deriveTitle(payload)
    payload.title = `[${APP_NAME}]${title ? ` ${title}` : ""}`

    payload.custom = {
      ...payload.custom,
      ApplicationName: APP_NAME,
      Tags: APP_TAGS,
    }

    const claims = decodeJwtPayload(tokenManager.getLastToken() ?? "")
    if (claims) {
      const profileId = claims["SecurityContactProfileId"]
      const tenantName = claims["TenantName"]
      const userName = claims["SecurityContactUserName"]
      const databaseId = claims["DatabaseId"]
      const environmentName = claims["EnvironmentName"]

      if (profileId != null && tenantName != null) {
        payload.person = {
          id: `${profileId}:${tenantName}`,
          username: userName,
          email: userName,
        }
      }
      payload.custom = {
        ...payload.custom,
        ProfileId: profileId,
        DatabaseId: databaseId,
        TenantName: tenantName,
        EnvironmentName: environmentName,
      }
    }
  },
})
